import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import {
  CalAm,
  CalAmInit,
  getInitPopUpData,
  getPlanDt,
} from "@shared/utils/CommonUtils";
import {
  CellValueChangedEvent,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnForm from "@widgets/SnForm";
import { showDialog } from "@widgets/CommonDialog";
import { dateToString } from "@shared/utils/DateUtils";
import { fnSave } from "@shared/api/restApi";
import { RowNode } from "ag-grid-community";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
  maxNoLine?: any;
}

const SN_PUR_POR_REG_002_POP_01 = forwardRef(
  ({ selectedData, modalRef, maxNoLine }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();

    const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      freeform1: React.createRef(),
    });
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
    });

    const [formControls, setFormControls] = useState<any>({
      freeform1: [],
    });
    const [formDatas, setFormDatas] = useState<any>({
      freeform1: null,
    });
    const [columns, setColums] = useState<any>({
      grid1: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
    });

    const saveDataRef = useRef<any>();
    const noLineRef = useRef<number>(maxNoLine);
    const dtShipPlanRef = useRef<string>("");
    const qtRef = useRef<number>(selectedData?.qt || 0);
    const qtReqRef = useRef<number>(selectedData?.qt_req || 0);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        getGridCurrent("grid1").stopEditing();

        const requiredValuesCheckH =
          getGridCurrent("grid1").requiredValuesCheck();
        if (!requiredValuesCheckH) return false;

        const solChanges = getGridCurrent("grid1").getChange();
        if (solChanges.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }

        // 수량이 0인 데이터가 있는지 검증
        // 1. 뒤에서부터 수량이 0인 행 스킵
        const rowCount = getGridCurrent("grid1").getDisplayedRowCount();
        let lastIdx = rowCount - 1;
        while (lastIdx >= 0) {
          const node = getGridCurrent("grid1").getRowNodeAtIndex(lastIdx);
          const qt = Number(node.data.qt || 0);
          if (qt !== 0) break;
          lastIdx--;
        }
        if (lastIdx === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }

        // 2. 남은 행에서 수량 0 검증
        let hasZeroQty = false;
        for (let i = 0; i <= lastIdx; i++) {
          const node = getGridCurrent("grid1").getRowNodeAtIndex(i);
          if (!node?.data) continue;

          const qt = Number(node.data.qt || 0);
          if (qt === 0) {
            hasZeroQty = true;
            break;
          }
        }
        if (hasZeroQty) {
          await showDialog("alter", "수량이 0인 행이 존재합니다.");
          return false;
        }

        // 플러스 수량, 마이너스 수량 둘 다 존재하는지
        let hasPositive = false;
        let hasNegative = false;
        for (let i = 0; i <= lastIdx; i++) {
          const node = getGridCurrent("grid1").getRowNodeAtIndex(i);
          if (!node?.data) continue;

          const qt = Number(node.data.qt || 0);
          if (qt > 0) hasPositive = true;
          if (qt < 0) hasNegative = true;
          if (hasPositive && hasNegative) break;
        }
        if (hasPositive && hasNegative) {
          const confirm = await showDialog(
            "confirm",
            "수량에 양수와 음수 모두 존재합니다.\n입력된 수량으로 분할하는 것이 맞습니까?"
          );
          if (!confirm) return false;
        }

        return await onSave();
      },
      getOkayData: async () => {
        return {
          rtnData: [saveDataRef.current],
        };
      },
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PUR_POR_REG_002_POP_01",
            {}
          );

          setFormControls({
            freeform1: rtn.formControlData,
          });
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormDatas({
            freeform1: {
              ...mergedData,
              qt_remain: qtRef.current - qtReqRef.current,
            },
          });
          setColums({
            grid1: rtn.grid.grid1,
          });
          const data = {
            ...selectedData,
            qt: qtReqRef.current === 0 ? qtRef.current : qtReqRef.current,
          };
          const result = await CalAmInit(data);
          const data2 = { ...data, ...result };
          setDataSources({
            grid1: (data2 && [data2]) || [],
          });
        } catch (error) {}
      };
      fetchform();
    }, []);

    const getGridCurrent = (key: string): SnGridRef => {
      const grid = gridRefs.current[key]?.current;
      if (!grid) {
        throw new Error(`Grid '${key}' is not mounted yet`);
      }
      return grid;
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "addRow") {
        onInsert();
      }
      if (id == "deleteRow") {
        onDelete();
      }
    };

    const onInsert = async () => {
      try {
        getGridCurrent("grid1").stopEditing();
        dtShipPlanRef.current = await getPlanDt(dateToString(new Date()), -1);

        getGridCurrent("grid1").addRow({});
      } catch (error: any) {}
    };

    const onBeforeAddRow = () => {
      try {
        const today = dateToString(new Date());
        let initData = { ...selectedData };
        initData.KeyField = null;
        initData.dt_dlv = today;
        initData.dt_ship_plan = dtShipPlanRef.current;
        initData.no_line = noLineRef.current + 1;
        initData.no_seq_sort = noLineRef.current + 1;
        initData.st_po = "R";
        initData.qt = 0;
        initData.am = 0;
        initData.am_k = 0;
        initData.am_vat = 0;
        initData.am_supply = 0;
        initData.um_k = 0;
        initData.um_supply = 0;
        initData.qt_req = 0;
        initData.version = 0;

        noLineRef.current = noLineRef.current + 1;

        return initData;
      } catch (error: any) {
        return false;
      }
    };

    const onAfterAddRow = async (node: RowNode) => {
      const footerData = getGridCurrent("grid1").getFooterData();
      const newQt = qtRef.current - footerData.qt;
      node.setDataValue("qt", newQt);
    };

    // const onInsert = async () => {
    //   try {
    //     getGridCurrent("grid1").stopEditing();
    //     dtShipPlanRef.current = await getPlanDt(dateToString(new Date()), -1);

    //     const footerData = getGridCurrent("grid1").getFooterData();
    //     const today = dateToString(new Date());
    //     let initData = selectedData;
    //     initData.KeyField = null;
    //     initData.dt_dlv = today;
    //     initData.dt_ship_plan = dtShipPlanRef.current;
    //     initData.no_line = noLineRef.current + 1;
    //     initData.no_seq_sort = noLineRef.current + 1;
    //     initData.st_po = "R";
    //     initData.qt = qtRef.current - footerData.qt;
    //     initData.am = 0;
    //     initData.am_k = 0;
    //     initData.am_vat = 0;
    //     initData.am_supply = 0;
    //     initData.um_k = 0;
    //     initData.um_supply = 0;
    //     initData.qt_req = 0;
    //     initData.version = 0;

    //     noLineRef.current = noLineRef.current + 1;
    //     const result = await CalAmInit(initData);

    //     getGridCurrent("grid1").addRow({ ...initData, ...result });
    //   } catch (error: any) {}
    // };

    const onDelete = async () => {
      const rowCount = getGridCurrent("grid1")?.getDisplayedRowCount();
      if (rowCount === 0) return;

      const focusedNode = getGridCurrent("grid1").focusedRowNode();
      if (focusedNode.data.KeyField === selectedData.KeyField) {
        await showDialog("alter", "기준 행은 삭제할 수 없습니다.");
        return;
      }

      gridRefs.current["grid1"].current?.deleteSelectedRows();
      noLineRef.current = getGridCurrent("grid1").getMaxValue("no_line");
    };

    const onSave = async () => {
      try {
        const polChanges = getGridCurrent("grid1").getChange();

        let insertList = polChanges.insert || [];
        // 뒤에서부터 qt 0 제거
        let lastIdx = insertList.length - 1;
        while (lastIdx >= 0) {
          const qt = Number(insertList[lastIdx]?.qt || 0);
          if (qt !== 0) break;
          lastIdx--;
        }
        insertList = insertList.slice(0, lastIdx + 1);

        const baseNode = getGridCurrent("grid1").getRowNodeAtIndex(0);
        const baseRowData = baseNode.data;
        const updateList =
          polChanges.update.length === 0 ? [baseRowData] : polChanges.update;

        polChanges.insert = insertList;
        polChanges.update = updateList;

        const saveData = {
          pur_pol: polChanges,
        };

        const ok = await fnSave("SN_PUR_POR_REG_001_SAVE_01", saveData);
        if (!ok) {
          getGridCurrent("grid1").resetUpdate(true);
          return false;
        }

        getGridCurrent("grid1").resetUpdate();
        saveDataRef.current = polChanges;
        return true;
      } catch (error: any) {
        getGridCurrent("grid1").resetUpdate(true);
        return false;
      }
    };

    const onCellValueChanged = async (event: CellValueChangedEvent) => {
      if (!event) return;

      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;

      if (colId === "dt_dlv") {
        if (!rowData.dt_dlv) return;

        const dt_ship_plan = await getPlanDt(rowData.dt_dlv, -1);
        rowNode.setDataValue("dt_ship_plan", dt_ship_plan);
      }
      if (colId === "qt") {
        // 전체 수량이 수주량과 일치하도록 마지막 행의 qt 보정
        const rowCount = getGridCurrent("grid1").getDisplayedRowCount();
        let sum = 0;
        getGridCurrent("grid1").forEachNode((node: RowNode) => {
          if (!node.data) return;
          if (node.rowIndex === rowCount - 1) return;
          sum += Number(node.data.qt || 0);
        });

        const lastNode = getGridCurrent("grid1").getRowNodeAtIndex(
          rowCount - 1
        );
        const newQt = qtRef.current - sum;
        const calResult = await CalAmInit({
          ...lastNode.data,
          qt: newQt,
        });

        lastNode.setDataValue("qt", newQt, "noEvent");
        lastNode.setDataValue("am", calResult.am, "noEvent");
        lastNode.setDataValue("am_k", calResult.am_k, "noEvent");
        lastNode.setDataValue("am_vat", calResult.am_vat, "noEvent");
        lastNode.setDataValue("am_supply", calResult.am_supply, "noEvent");
        lastNode.setDataValue("um_k", calResult.um_k, "noEvent");
        lastNode.setDataValue("um_supply", calResult.um_supply, "noEvent");
      }
      await CalAm(null, gridRefs.current["grid1"], colId);
    };

    const forms = useMemo(
      () => ({
        freeform1: {
          controls: formControls.freeform1,
          data: formDatas.freeform1,
          etcProps: {},
          events: {},
        },
      }),
      [formControls, formDatas]
    );

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onBtnAddClick: onGrid1BtnsClick,
            onBtnDeleteClick: onGrid1BtnsClick,
            onCellValueChanged: onCellValueChanged,
            onBeforeAddRow: onBeforeAddRow,
            onAfterAddRow: onAfterAddRow,
          },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="h-full flex flex-col">
        <div className="h-[180px] mb-1">
          <SnForm
            ref={formRefs.current["freeform1"]}
            gridref={gridRefs.current["grid1"]}
            data={forms.freeform1.data}
            controls={forms.freeform1.controls}
            events={forms.freeform1.events}
          />
        </div>
        <div className="flex-1">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "상세정보",
              entertoAddrow: true,
            }}
            events={grids.grid1.events}
          />
        </div>
      </div>
    );
  }
);

export default SN_PUR_POR_REG_002_POP_01;
